import { cn } from "@/lib/cn";
import type { VocabularyEntry } from "@/lib/phaseVocabulary";
import type { PredicateDocument, PredicateLeafDocument, PredicateOp } from "@/lib/types";
import { INPUT_CLS, SMALL_BUTTON_CLS } from "@/components/strategy/FieldEditors";

/**
 * Structured editor for the compound predicate grammar the transition engine
 * evaluates: groups of `all` / `any` / `not` around `{field, op, value}`
 * leaves. Every change hands the whole predicate back; `null` means "no
 * condition" (the rule always fires).
 *
 * The editor renders any depth the document contains, so nothing an operator
 * wrote in YAML is hidden — only the field list is curated, from the engine's
 * documented roots plus the strategy's own declared keys.
 */

const OPS: { value: PredicateOp; label: string; kind: "scalar" | "list" | "bool" | "none" }[] = [
  { value: "eq", label: "equals", kind: "scalar" },
  { value: "neq", label: "does not equal", kind: "scalar" },
  { value: "gt", label: "is greater than", kind: "scalar" },
  { value: "gte", label: "is at least", kind: "scalar" },
  { value: "lt", label: "is less than", kind: "scalar" },
  { value: "lte", label: "is at most", kind: "scalar" },
  { value: "in", label: "is one of", kind: "list" },
  { value: "not_in", label: "is none of", kind: "list" },
  { value: "exists", label: "has a value", kind: "bool" },
  { value: "matched", label: "was detected", kind: "scalar" },
];

type GroupKind = "all" | "any" | "not";

export function isLeaf(p: PredicateDocument): p is PredicateLeafDocument {
  return typeof (p as PredicateLeafDocument).field === "string";
}

function groupKind(p: PredicateDocument): GroupKind | null {
  if ("all" in p && Array.isArray(p.all)) return "all";
  if ("any" in p && Array.isArray(p.any)) return "any";
  if ("not" in p) return "not";
  return null;
}

function newLeaf(fields: VocabularyEntry[]): PredicateLeafDocument {
  return { field: fields[0]?.path ?? "", op: "eq", value: "" };
}

/**
 * A value typed the way the engine compares it: numbers stay numbers so a
 * `gt` comparison is numeric, booleans stay booleans for `exists`, and
 * everything else is text. YAML authors get the same coercion for free.
 */
function coerce(raw: string): unknown {
  const t = raw.trim();
  if (t === "") return "";
  if (t === "true") return true;
  if (t === "false") return false;
  if (/^-?\d+(\.\d+)?$/.test(t)) return Number(t);
  return raw;
}

function display(v: unknown): string {
  if (v === undefined || v === null) return "";
  if (Array.isArray(v)) return v.map(display).join(", ");
  return typeof v === "object" ? JSON.stringify(v) : String(v);
}

export default function PredicateEditor({
  value,
  onChange,
  fields,
  conditionNames,
  label,
  disabled,
}: {
  value: PredicateDocument | null | undefined;
  onChange: (value: PredicateDocument | null) => void;
  fields: VocabularyEntry[];
  /** Names the reply classifier can produce, for `matched_conditions`. */
  conditionNames: string[];
  /** Accessible prefix for this editor's controls, e.g. "Rule 2". */
  label: string;
  disabled?: boolean;
}) {
  if (value === null || value === undefined) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[var(--fg-muted)]">always fires (no condition)</span>
        {!disabled ? (
          <button
            type="button"
            className={SMALL_BUTTON_CLS}
            onClick={() => onChange(newLeaf(fields))}
          >
            Add condition
          </button>
        ) : null}
      </div>
    );
  }
  return (
    <PredicateNode
      value={value}
      onChange={onChange}
      fields={fields}
      conditionNames={conditionNames}
      label={label}
      disabled={disabled}
      root
    />
  );
}

function PredicateNode({
  value,
  onChange,
  fields,
  conditionNames,
  label,
  disabled,
  root,
}: {
  value: PredicateDocument;
  onChange: (value: PredicateDocument | null) => void;
  fields: VocabularyEntry[];
  conditionNames: string[];
  label: string;
  disabled?: boolean;
  root?: boolean;
}) {
  const kind = groupKind(value);

  if (isLeaf(value)) {
    return (
      <LeafRow
        leaf={value}
        onChange={onChange}
        fields={fields}
        conditionNames={conditionNames}
        label={label}
        disabled={disabled}
        onWrap={
          root
            ? (g) => onChange(g === "not" ? { not: value } : g === "all" ? { all: [value, newLeaf(fields)] } : { any: [value, newLeaf(fields)] })
            : undefined
        }
      />
    );
  }

  if (kind === "not") {
    const inner = (value as { not: PredicateDocument }).not;
    return (
      <div className="rounded border border-dashed border-[var(--border)] p-1.5">
        <div className="mb-1 flex items-center gap-2">
          <GroupSelect
            value="not"
            label={label}
            disabled={disabled}
            onChange={(g) => {
              if (g === "not") return;
              onChange(g === "all" ? { all: [inner] } : { any: [inner] });
            }}
          />
          <span className="text-[var(--fg-muted)]">the following is true</span>
          {!disabled ? (
            <button
              type="button"
              aria-label={`Remove ${label} group`}
              className="ml-auto text-[var(--fg-muted)] hover:text-[var(--danger)]"
              onClick={() => onChange(inner)}
            >
              ×
            </button>
          ) : null}
        </div>
        <div className="pl-3">
          <PredicateNode
            value={inner}
            onChange={(next) => onChange(next === null ? null : { not: next })}
            fields={fields}
            conditionNames={conditionNames}
            label={`${label} inner`}
            disabled={disabled}
          />
        </div>
      </div>
    );
  }

  if (kind === "all" || kind === "any") {
    const items = (value as Record<GroupKind, PredicateDocument[]>)[kind];
    const rebuild = (next: PredicateDocument[]): PredicateDocument | null =>
      next.length === 0 ? null : ({ [kind]: next } as PredicateDocument);
    return (
      <div className="rounded border border-dashed border-[var(--border)] p-1.5">
        <div className="mb-1 flex items-center gap-2">
          <GroupSelect
            value={kind}
            label={label}
            disabled={disabled}
            onChange={(g) => {
              if (g === kind) return;
              if (g === "not") onChange({ not: items.length === 1 ? (items[0] as PredicateDocument) : value });
              else onChange({ [g]: items } as PredicateDocument);
            }}
          />
          <span className="text-[var(--fg-muted)]">of the following</span>
          {!disabled ? (
            <button
              type="button"
              aria-label={`Remove ${label} group`}
              className="ml-auto text-[var(--fg-muted)] hover:text-[var(--danger)]"
              onClick={() => onChange(null)}
            >
              ×
            </button>
          ) : null}
        </div>
        <div className="space-y-1 pl-3">
          {items.map((item, i) => (
            <PredicateNode
              key={i}
              value={item}
              onChange={(next) => {
                const list = [...items];
                if (next === null) list.splice(i, 1);
                else list[i] = next;
                onChange(rebuild(list));
              }}
              fields={fields}
              conditionNames={conditionNames}
              label={`${label} condition ${i + 1}`}
              disabled={disabled}
            />
          ))}
          {!disabled ? (
            <div className="flex gap-1">
              <button
                type="button"
                className={SMALL_BUTTON_CLS}
                onClick={() => onChange(rebuild([...items, newLeaf(fields)]))}
              >
                Add condition
              </button>
              <button
                type="button"
                className={SMALL_BUTTON_CLS}
                onClick={() => onChange(rebuild([...items, { all: [newLeaf(fields)] }]))}
              >
                Add group
              </button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  // A shape the grammar does not define (an authoring slip in the YAML).
  // Show it rather than silently rewriting it; the YAML editor fixes it.
  return (
    <pre className="overflow-x-auto rounded bg-zinc-50 p-1.5 text-[10px] dark:bg-zinc-900">
      {JSON.stringify(value, null, 2)}
    </pre>
  );
}

function GroupSelect({
  value,
  onChange,
  label,
  disabled,
}: {
  value: GroupKind;
  onChange: (g: GroupKind) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <select
      aria-label={`${label} group type`}
      className="rounded border border-[var(--border)] bg-[var(--bg-card)] px-1 py-0.5 text-xs"
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as GroupKind)}
    >
      <option value="all">All</option>
      <option value="any">Any</option>
      <option value="not">Not</option>
    </select>
  );
}

function LeafRow({
  leaf,
  onChange,
  fields,
  conditionNames,
  label,
  disabled,
  onWrap,
}: {
  leaf: PredicateLeafDocument;
  onChange: (value: PredicateDocument | null) => void;
  fields: VocabularyEntry[];
  conditionNames: string[];
  label: string;
  disabled?: boolean;
  onWrap?: (g: GroupKind) => void;
}) {
  const op = OPS.find((o) => o.value === leaf.op) ?? OPS[0]!;
  const known = fields.some((f) => f.path === leaf.field);
  const isMatched = leaf.field === "matched_conditions";

  const groups = new Map<string, VocabularyEntry[]>();
  for (const f of fields) {
    const list = groups.get(f.group) ?? [];
    list.push(f);
    groups.set(f.group, list);
  }

  const setValue = (raw: string) => {
    if (op.kind === "list") {
      onChange({ ...leaf, value: raw.split(",").map((s) => coerce(s.trim())).filter((v) => v !== "") });
    } else onChange({ ...leaf, value: coerce(raw) });
  };

  return (
    <div className="flex flex-wrap items-center gap-1">
      <select
        aria-label={`${label} field`}
        className="max-w-[12rem] rounded border border-[var(--border)] bg-[var(--bg-card)] px-1 py-0.5 text-xs"
        value={leaf.field}
        disabled={disabled}
        onChange={(e) => {
          const field = e.target.value;
          const next: PredicateLeafDocument = { ...leaf, field };
          if (field === "matched_conditions") {
            next.op = "matched";
            next.value = conditionNames[0] ?? "";
          } else if (leaf.op === "matched") {
            next.op = "eq";
            next.value = "";
          }
          onChange(next);
        }}
      >
        {!known ? <option value={leaf.field}>{leaf.field || "(choose a field)"}</option> : null}
        {[...groups.entries()].map(([group, entries]) => (
          <optgroup key={group} label={group}>
            {entries.map((f) => (
              <option key={f.path} value={f.path}>{f.label}</option>
            ))}
          </optgroup>
        ))}
      </select>

      <select
        aria-label={`${label} operator`}
        className="rounded border border-[var(--border)] bg-[var(--bg-card)] px-1 py-0.5 text-xs"
        value={op.value}
        disabled={disabled}
        onChange={(e) => {
          const nextOp = OPS.find((o) => o.value === e.target.value) ?? OPS[0]!;
          const next: PredicateLeafDocument = { ...leaf, op: nextOp.value };
          if (nextOp.kind === "bool") next.value = true;
          else if (nextOp.kind === "list" && !Array.isArray(leaf.value)) next.value = [];
          else if (nextOp.kind !== "list" && Array.isArray(leaf.value)) next.value = leaf.value[0] ?? "";
          onChange(next);
        }}
      >
        {OPS.filter((o) => (isMatched ? o.value === "matched" : o.value !== "matched")).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      {op.kind === "bool" ? (
        <select
          aria-label={`${label} value`}
          className="rounded border border-[var(--border)] bg-[var(--bg-card)] px-1 py-0.5 text-xs"
          value={leaf.value === false ? "false" : "true"}
          disabled={disabled}
          onChange={(e) => onChange({ ...leaf, value: e.target.value === "true" })}
        >
          <option value="true">yes</option>
          <option value="false">no</option>
        </select>
      ) : isMatched && conditionNames.length > 0 ? (
        <select
          aria-label={`${label} value`}
          className="max-w-[12rem] rounded border border-[var(--border)] bg-[var(--bg-card)] px-1 py-0.5 text-xs"
          value={display(leaf.value)}
          disabled={disabled}
          onChange={(e) => onChange({ ...leaf, value: e.target.value })}
        >
          {!conditionNames.includes(display(leaf.value)) ? (
            <option value={display(leaf.value)}>{display(leaf.value) || "(choose)"}</option>
          ) : null}
          {conditionNames.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      ) : (
        <input
          aria-label={`${label} value`}
          className={cn(INPUT_CLS, "max-w-[12rem] py-0.5 text-xs")}
          defaultValue={display(leaf.value)}
          key={`${leaf.field}:${leaf.op}`}
          placeholder={op.kind === "list" ? "a, b, c" : "value"}
          disabled={disabled}
          onBlur={(e) => {
            if (e.target.value !== display(leaf.value)) setValue(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
        />
      )}

      {!disabled ? (
        <>
          {onWrap ? (
            <button
              type="button"
              className={SMALL_BUTTON_CLS}
              title="Combine with another condition"
              onClick={() => onWrap("all")}
            >
              + and
            </button>
          ) : null}
          <button
            type="button"
            aria-label={`Remove ${label}`}
            className="text-[var(--fg-muted)] hover:text-[var(--danger)]"
            onClick={() => onChange(null)}
          >
            ×
          </button>
        </>
      ) : null}
    </div>
  );
}
